import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Dialog, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

import { Transition } from "../../UiComponents/Transition/Transition";
import { PostClientWalletMinimumBalance } from "../../Service/_index";
import Styles from "./ClientCompanyWallet.module.scss";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const validationSchema = Yup.object().shape({
  minimumBalance: Yup.number()
    .typeError("Minimum balance must be a number")
    .min(0, "Minimum balance cannot be negative")
    .max(10000000, "That looks too large — please check")
    .required("Minimum balance is required"),
});

const SetMinimumBalanceDialog = ({ open, company, clientId, clientName, currentMinimum, onClose, onSaved }) => {
  const handleClose = () => onClose();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{ sx: { maxWidth: "420px !important", width: "100%", borderRadius: "10px" } }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between" }}
        className={Styles.DialogHeader}
      >
        <Box>
          <Typography className={Styles.DialogTitle}>Set Minimum Balance</Typography>
          <Typography className={Styles.DialogSubtitle}>
            {clientName} &middot; {company}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "white" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Formik
        initialValues={{ minimumBalance: currentMinimum || 0 }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          PostClientWalletMinimumBalance({
            clientId,
            companyName: company,
            minimumBalance: Number(values.minimumBalance),
          })
            .then((res) => {
              if (res && !res.error) onSaved();
            })
            .catch(() => {})
            .finally(() => setSubmitting(false));
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <Box sx={{ display: "flex", flexDirection: "column" }} className={Styles.DialogBody}>
              <Typography sx={{ fontSize: 12.5, color: "#667085", mb: 2 }}>
                You will be warned in the bell at the top of the page when this wallet&apos;s balance is at or
                below this amount. Enter 0 to turn the warning off.
              </Typography>

              <Typography className={Styles.FieldLabel}>Minimum Balance ({company})</Typography>
              <Field
                name="minimumBalance"
                type="number"
                autoFocus
                className={Styles.InputFiled}
                placeholder="Enter minimum balance"
              />
              {touched.minimumBalance && errors.minimumBalance && (
                <Typography className={Styles.FieldError}>{errors.minimumBalance}</Typography>
              )}
              {currentMinimum > 0 && (
                <Typography sx={{ fontSize: 11.5, color: "#98A2B3", mt: 0.5 }}>
                  Current: &#8377; {money(currentMinimum)}
                </Typography>
              )}
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }}
              className={Styles.DialogFooter}
            >
              <Button
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: "6px" }}
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                className={Styles.TopupButton}
                startIcon={<SaveIcon sx={{ fontSize: "16px !important" }} />}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SetMinimumBalanceDialog;
